import { useEffect, useState } from 'react';
import { publishersManageService } from '../../../api/publishersManageService';
import { useBulkPublisherActions } from '../../../hooks/useBulkPublisherActions';
import type {
  BulkPublisherOperationResultDto,
  BulkPublisherQuotaUpdateDto,
} from '../../../types/managePublishers';
import { useTranslation } from '../../../i18n/useTranslation';

interface Props {
  open: boolean;
  publisherIds: string[];
  onClose: () => void;
  onCompleted: (result: BulkPublisherOperationResultDto) => void;
}

type QuotaForm = Omit<BulkPublisherQuotaUpdateDto, 'publisherIds' | 'alsoUpdateBooks'>;

const EMPTY: QuotaForm = {};

export default function PublisherQuotaEditDialog({
  open, publisherIds, onClose, onCompleted,
}: Props) {
  const { t } = useTranslation();
  const [form, setForm] = useState<QuotaForm>(EMPTY);
  const [alsoUpdateBooks, setAlsoUpdateBooks] = useState(true);
  const [bookCount, setBookCount] = useState<number | null>(null);
  const { bulkQuotas } = useBulkPublisherActions();

  useEffect(() => {
    if (!open) return;
    setForm(EMPTY);
    setAlsoUpdateBooks(true);
    setBookCount(null);
    publishersManageService.affectedBookCount(publisherIds)
      .then(setBookCount)
      .catch(() => setBookCount(null));
  }, [open, publisherIds]);

  if (!open) return null;

  const submit = async () => {
    const result = await bulkQuotas.mutateAsync({
      publisherIds,
      alsoUpdateBooks,
      ...form,
    });
    onCompleted(result);
    onClose();
  };

  const num = (k: keyof QuotaForm) => ({
    type: 'number' as const,
    min: 0,
    value: form[k] ?? '',
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
      const v = e.target.value === '' ? undefined : Number(e.target.value);
      setForm({ ...form, [k]: v });
    },
    className: 'border border-neutral-300 rounded px-2 py-1 text-sm w-full',
  });

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg max-w-lg w-full p-5">
        <h2 className="text-lg font-semibold mb-1">{t('App.Admin.Publishers.PublisherQuotaEditDialog.Title', { count: publisherIds.length })}</h2>
        <p className="text-xs text-neutral-500 mb-4">{t('App.Admin.Publishers.PublisherQuotaEditDialog.Hint')}</p>

        <div className="grid grid-cols-2 gap-3 mb-4">
          <label className="text-xs text-neutral-600">{t('App.Admin.Publishers.PublisherQuotaEditDialog.FieldPrintLimit')}<input {...num('defaultPrintQuotaLimit')} /></label>
          <label className="text-xs text-neutral-600">{t('App.Admin.Publishers.PublisherQuotaEditDialog.FieldPrintWindow')}<input {...num('defaultPrintQuotaWindowDays')} /></label>
          <label className="text-xs text-neutral-600">{t('App.Admin.Publishers.PublisherQuotaEditDialog.FieldCopyLimit')}<input {...num('defaultCopyQuotaLimit')} /></label>
          <label className="text-xs text-neutral-600">{t('App.Admin.Publishers.PublisherQuotaEditDialog.FieldCopyWindow')}<input {...num('defaultCopyQuotaWindowDays')} /></label>
          <label className="text-xs text-neutral-600">{t('App.Admin.Publishers.PublisherQuotaEditDialog.FieldDownloadLimit')}<input {...num('defaultDownloadQuotaLimit')} /></label>
          <label className="text-xs text-neutral-600">{t('App.Admin.Publishers.PublisherQuotaEditDialog.FieldDownloadWindow')}<input {...num('defaultDownloadQuotaWindowDays')} /></label>
        </div>

        <label className="flex items-start gap-2 text-sm mb-4">
          <input
            type="checkbox"
            checked={alsoUpdateBooks}
            onChange={(e) => setAlsoUpdateBooks(e.target.checked)}
            className="mt-0.5"
          />
          <span>
            {t('App.Admin.Publishers.PublisherQuotaEditDialog.AlsoUpdateBooks')}
            {bookCount !== null && <span className="text-neutral-500"> {t('App.Admin.Publishers.PublisherQuotaEditDialog.AffectedBooksCount', { count: bookCount })}</span>}
          </span>
        </label>

        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-3 py-1 border border-neutral-300 rounded hover:bg-neutral-100">{t('App.Admin.Publishers.PublisherQuotaEditDialog.Cancel')}</button>
          <button
            type="button"
            onClick={submit}
            disabled={bulkQuotas.isPending}
            className="px-3 py-1 bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50"
          >
            {t('App.Admin.Publishers.PublisherQuotaEditDialog.UpdateQuotas')}
          </button>
        </div>
      </div>
    </div>
  );
}
